import { useState, useEffect } from "react";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import classNames from "classnames";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { InfoIcon } from "lucide-react";

interface UpdateBannerProps {
  bannerId: string;
  header?: string;
  message?: string;
  className?: string;
  onDismiss?: () => void;
  expirationDate?: Date;
}

/**
 * A dismissible banner component that informs users of updates.
 * Uses localStorage to remember dismissal state across sessions.
 */
export const UpdateBanner = ({
  bannerId,
  header,
  message,
  className,
  onDismiss,
  expirationDate,
}: UpdateBannerProps) => {
  const { t } = useModifiedTranslation();

  const [isVisible, setIsVisible] = useState(() => {
    try {
      if (typeof window !== "undefined") {
        // Check if banner has expired
        if (expirationDate && new Date() > expirationDate) {
          return false;
        }

        const storageKey = `banner-dismissed-${bannerId}`;
        const dismissed = localStorage.getItem(storageKey);
        const shouldShow = dismissed !== "true";
        return shouldShow;
      }
    } catch (e) {
      console.error("Error reading localStorage:", e);
    }
    return true;
  });

  useEffect(() => {
    // Check if banner has expired
    if (expirationDate && new Date() > expirationDate) {
      setIsVisible(false);
      return;
    }

    const storageKey = `banner-dismissed-${bannerId}`;
    const isDismissed = localStorage.getItem(storageKey) === "true";
    setIsVisible(!isDismissed);
  }, [bannerId, expirationDate]);

  const handleDismiss = () => {
    const storageKey = `banner-dismissed-${bannerId}`;
    localStorage.setItem(storageKey, "true");
    setIsVisible(false);
    onDismiss?.();
  };

  const translationKey = `banners.updates.${bannerId}`;
  const translatedMessage = t(translationKey);
  const displayMessage = message || translatedMessage;

  if (!isVisible) return null;

  if (!displayMessage || displayMessage === translationKey) {
    return null;
  }

  return (
    <div
      className={classNames(
        "flex items-center justify-between w-full bg-secondary-100 text-white-100 px-4 py-3 rounded-lg",
        className
      )}
      data-testid="update-banner"
    >
      <span>
        <InfoIcon className="mr-4 w-10 h-10 text-white-100" />
      </span>
      <div>
        <h3 className="text-headline-3 mb-1 font-semibold text-white-100">
          {header}
        </h3>
        <p className="text-sm text-balance text-white-100">{message}</p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className="ml-4 hover:bg-secondary-200/20 cursor-pointer rounded-lg border-2 border-transparent outline-none focus:border-white-100/50 transition-colors p-1"
        aria-label={t("banners.dismiss", { defaultValue: "Dismiss" })}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "4px",
        }}
      >
        <CloseIcon
          className="w-5 h-5 fill-white-100"
          style={{ fill: "#ffffff" }}
        />
      </button>
    </div>
  );
};
